import { COUNTRY_ISO } from 'constants/country';

// The Cognito Forms organisation URL (everything up to and including the org key) is fixed
// per environment and lives in REACT_APP_COGNITO_FORMS_BASE_URL. Only the trailing form id
// is configurable per product/region.
export const COGNITO_FORMS_BASE_URL = process.env.REACT_APP_COGNITO_FORMS_BASE_URL || '';

export const buildCognitoFormUrl = (formId: string): string =>
  `${COGNITO_FORMS_BASE_URL.replace(/\/+$/, '')}/${encodeURIComponent(formId.trim())}`;

export type AffiliateFormRegion = 'US' | 'CA';

// One entry per region — label and country ISO live together so adding a region is a single
// edit. countryIso resolves to the actual countryId (ProductCognitoForm's real key) against
// the loaded countries list at render time, since country ids are DB-assigned, not static.
export const REGIONS: Record<AffiliateFormRegion, { label: string; countryIso: string }> = {
  US: { label: 'United States (US)', countryIso: COUNTRY_ISO.US },
  CA: { label: 'Canada (CA)', countryIso: COUNTRY_ISO.CA }
};

export const AFFILIATE_FORM_REGIONS = Object.keys(REGIONS) as AffiliateFormRegion[];

export const AFFILIATE_FORM_LABELS = {
  TAB: 'Affiliate form',

  CONFIG_TITLE: 'Affiliate form configuration',
  CONFIG_SUBTITLE: 'Configure Cognito forms for each region where this product will be sold.',

  FORM_ID_LABEL: 'Cognito form ID',
  FORM_ID_PLACEHOLDER: 'Enter Cognito form ID',
  APPLY: 'Apply form',
  APPLYING_STATUS: 'Applying form...',
  PREVIEW_LOADED: 'Preview loaded',
  REPLACE_HINT: 'Entering a new form ID will replace the currently configured form for this region.',

  PREVIEW_TITLE: 'Checkout form preview',
  PREVIEW_SELECT_REGION: 'Select region to preview',
  PREVIEW_LOADING: 'Loading Affiliate form preview...',
  PREVIEW_INFO: 'The preview shows the form that Buyers will see during the checkout Documents step for the selected region.',

  EMPTY_TITLE: 'No form added yet.',
  EMPTY_DESCRIPTION:
    'Enter a Cognito form ID for the selected region above and click Apply form to add the Affiliate form and see the preview.',

  // AC 9's three failure cases. A cross-origin iframe alone can't tell a bad form id apart
  // from a network failure — `load` fires either way regardless of the response status. The
  // GET existence check in AffiliateFormView (Cognito's own endpoint sends CORS headers)
  // resolves that ambiguity before the iframe is ever pointed at the candidate id.
  ERROR_REQUIRED: 'Enter a Cognito form ID.',
  ERROR_INVALID_FORM_ID: 'We could not load a form using this Cognito form ID. Check the ID and try again.',
  ERROR_UNAVAILABLE: 'The Affiliate form could not be loaded. Please try again.',
  ERROR_NOT_CONFIGURED:
    'Affiliate form previews are not configured for this environment. Contact your administrator.',

  // Shown on the disabled Submit button while any region's form id is in error.
  SUBMIT_BLOCKED:
    'This product may not be submitted until the Affiliate form ID error has been resolved.',
  SAVE_FORM_NOT_APPLIED:
    'The Affiliate form ID could not be validated, so it was not applied. Your other changes are being saved and the previously configured form ID is unchanged.'
};

export const AFFILIATE_FORM_TOAST_ID = 'affiliate-form-not-applied';

// How long to wait for the form-id existence check before treating the attempt as a failed
// integration/network call. This bounds the `fetch`, not the preview iframe: the frame plays no
// part in deciding whether an id is good, so a slow frame cannot strand an apply.
export const AFFILIATE_FORM_LOAD_TIMEOUT_MS = 15000;
